import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import { sbg, typeStyle } from '../../theme/sbgTokens';
import AuthLayout, { AuthHeading, PasswordField, comfortFieldSx, looksLikeEmail } from './auth-parts/AuthLayout';

/** Shown while the direct token fallback runs; rendered as a notice, not an error. */
const SLOW_SIGN_IN = 'Sign-in is taking longer than usual. Trying another way to connect…';
const EMAIL_REQUIRED = 'Enter your work email.';
const EMAIL_INVALID = 'Enter a full email address, like name@sbgfunding.com.';
const PASSWORD_REQUIRED = 'Enter your password.';
const RESET_EMAIL_REQUIRED = 'Enter your work email so we know where to send the link.';
const RESET_EMAIL_SENT = 'Check your inbox for a link to set a new password. It can take a minute to arrive.';
const RESET_RATE_LIMITED = 'You can request another link in a minute.';
const TRY_AGAIN_SOON = 'Try again in a minute.';

interface SignInErrorCopy {
  title: string;
  body: string;
  /** Offer the reset-password dialog inline. */
  offerReset?: boolean;
}

/** What the sign-in region shows. The slow fallback is a notice, not a failure. */
type SignInState = { kind: 'idle' } | { kind: 'slow' } | { kind: 'failed'; copy: SignInErrorCopy };

/** What the forgot-password dialog shows. */
type ResetState =
  | { kind: 'idle' }
  | { kind: 'missingEmail' }
  | { kind: 'invalidEmail' }
  | { kind: 'sent' }
  | { kind: 'failed'; body: string };

interface FieldErrors {
  email?: string;
  password?: string;
}

interface LoginLocationState {
  from?: { pathname?: string };
  /** Set by ResetPassword's "Request a new link": open the reset dialog on arrival. */
  openReset?: boolean;
}

/**
 * Presentation only: turns raw sign-in failures (Supabase messages and the
 * fallback's own errors) into what happened + what to do. Raw messages are
 * never shown; the sign-in logic and the messages it raises are unchanged.
 */
const describeSignInError = (raw: string): SignInErrorCopy => {
  if (/invalid login credentials/i.test(raw)) {
    return { title: 'That email and password don’t match', body: 'Check both and try again, or', offerReset: true };
  }
  if (/email not confirmed/i.test(raw)) {
    return { title: 'Your email isn’t confirmed yet', body: 'Open the confirmation email we sent you, then sign in again.' };
  }
  if (/rate limit|too many/i.test(raw)) {
    return { title: 'Too many sign-in attempts', body: 'Wait a few minutes, then try again.' };
  }
  if (/missing supabase env/i.test(raw)) {
    return { title: 'Sign-in isn’t available on this site', body: 'Its sign-in settings are missing. Let HR know so they can fix it.' };
  }
  if (/timed out|stalled|storage|extension/i.test(raw)) {
    return {
      title: 'Sign-in didn’t finish',
      body: 'A browser extension or blocked site storage may be interfering. Try a private window, then sign in again.',
    };
  }
  if (/failed to fetch|network|load failed/i.test(raw)) {
    return { title: 'Couldn’t reach the sign-in service', body: 'Check your connection and try again.' };
  }
  return { title: 'Couldn’t sign you in', body: `${TRY_AGAIN_SOON} If it keeps happening, let HR know.` };
};

/** Presentation only: maps a reset-email failure to dialog state. */
const describeResetFailure = (raw: string): ResetState => {
  if (/invalid format|validate email|invalid email/i.test(raw)) return { kind: 'invalidEmail' };
  if (/rate limit|too many|security purposes/i.test(raw)) return { kind: 'failed', body: RESET_RATE_LIMITED };
  return { kind: 'failed', body: TRY_AGAIN_SOON };
};

const Login: React.FC = () => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LoginLocationState | null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signInState, setSignInState] = useState<SignInState>({ kind: 'idle' });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [reset, setReset] = useState<ResetState>({ kind: 'idle' });
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [resetOpen, setResetOpen] = useState(() => Boolean(locationState?.openReset));
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const resetEmailRef = useRef<HTMLInputElement>(null);

  // Default to the role-aware DefaultRedirect at '/': employees cannot open
  // /dashboard, so sending them there directly lands on the restricted screen.
  const from = locationState?.from?.pathname || '/';

  // Arriving from an expired reset link opens the dialog once; drop the flag
  // so a refresh or a later visit doesn't reopen it.
  useEffect(() => {
    if (locationState?.openReset) {
      navigate(location.pathname, { replace: true, state: locationState.from ? { from: locationState.from } : null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withTimeout = async <T,>(promise: Promise<T>, ms: number, message: string) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(message)), ms);
    });
    try {
      return await Promise.race([promise, timeout]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  const fallbackSignIn = async (emailValue: string, passwordValue: string) => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Missing Supabase env vars on the frontend.');
    }

    const controller = new AbortController();
    const response = await withTimeout(
      fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          apikey: supabaseKey,
          authorization: `Bearer ${supabaseKey}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ email: emailValue, password: passwordValue, gotrue_meta_security: {} }),
        signal: controller.signal,
      }),
      6000,
      'Fallback token request timed out. Check extensions or network.'
    );

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message = payload?.msg || payload?.error_description || payload?.error || 'Fallback sign-in failed';
      throw new Error(message);
    }

    const data = await response.json();
    if (!data?.access_token || !data?.refresh_token) {
      throw new Error('Fallback sign-in did not return session tokens.');
    }

    await withTimeout(
      supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      }),
      4000,
      'Saving session timed out. This is usually a browser extension or storage issue.'
    );
  };

  const focusAndSelect = (ref: React.RefObject<HTMLInputElement | null>) => {
    ref.current?.focus();
    ref.current?.select();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // The button stays focusable while signing in (aria-disabled), so guard re-entry here.
    if (loading) return;

    // Inline validation replaces the browser's own bubbles; the first problem gets focus.
    const errors: FieldErrors = {
      email: !email.trim() ? EMAIL_REQUIRED : !looksLikeEmail(email) ? EMAIL_INVALID : undefined,
      password: !password ? PASSWORD_REQUIRED : undefined,
    };
    if (errors.email || errors.password) {
      setFieldErrors(errors);
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }

    setFieldErrors({});
    setSignInState({ kind: 'idle' });
    setReset({ kind: 'idle' });
    setLoading(true);
    try {
      const timeoutMs = 6000;
      const result = await Promise.race([
        signIn(email, password).then(() => 'ok' as const),
        new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), timeoutMs)),
      ]);

      if (result === 'timeout') {
        setSignInState({ kind: 'slow' });
        await withTimeout(
          fallbackSignIn(email, password),
          12000,
          'Fallback sign-in stalled. Try incognito or disable extensions.'
        );
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setSignInState({ kind: 'failed', copy: describeSignInError(err?.message || 'Failed to sign in') });
      // Put the user back where the fix usually is: the password, selected for retyping.
      focusAndSelect(passwordRef);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setReset({ kind: 'missingEmail' });
      resetEmailRef.current?.focus();
      return;
    }
    if (!looksLikeEmail(resetEmail)) {
      setReset({ kind: 'invalidEmail' });
      focusAndSelect(resetEmailRef);
      return;
    }
    setResetLoading(true);
    setReset({ kind: 'idle' });
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) {
        throw resetError;
      }
      setReset({ kind: 'sent' });
    } catch (err: any) {
      const next = describeResetFailure(err?.message || '');
      setReset(next);
      if (next.kind === 'invalidEmail') focusAndSelect(resetEmailRef);
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [loading]);

  const openReset = () => {
    setResetEmail(email);
    setReset({ kind: 'idle' });
    setResetOpen(true);
  };

  const resetSent = reset.kind === 'sent';
  const resetFieldError =
    reset.kind === 'missingEmail' ? RESET_EMAIL_REQUIRED : reset.kind === 'invalidEmail' ? EMAIL_INVALID : null;
  const signInError = signInState.kind === 'failed' ? signInState.copy : null;
  const showSentOnPage = resetSent && !resetOpen && signInState.kind === 'idle';

  return (
    <AuthLayout>
      <AuthHeading title="Sign in" description="Use your work email and password." />

      {(signInState.kind !== 'idle' || showSentOnPage) && (
        <Box sx={{ mb: 3 }}>
          {signInState.kind === 'slow' && (
            <Alert severity="info" role="status">
              {SLOW_SIGN_IN}
            </Alert>
          )}
          {signInError && (
            <Alert severity="error">
              <AlertTitle>{signInError.title}</AlertTitle>
              {signInError.body}
              {signInError.offerReset && (
                <>
                  {' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={openReset}
                    sx={{ color: 'inherit', font: 'inherit', verticalAlign: 'baseline', '&:hover': { color: 'inherit' } }}
                  >
                    reset your password
                  </Link>
                  .
                </>
              )}
            </Alert>
          )}
          {showSentOnPage && (
            <Alert severity="success" role="status">
              <AlertTitle>Reset link sent</AlertTitle>
              {RESET_EMAIL_SENT}
            </Alert>
          )}
        </Box>
      )}

      <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2.5 }}>
        <TextField
          id="login-email"
          label="Work email"
          type="email"
          size="medium"
          placeholder="name@sbgfunding.com"
          autoComplete="username"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
          }}
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email}
          inputRef={emailRef}
          fullWidth
          sx={comfortFieldSx}
          slotProps={{ htmlInput: { required: true } }}
        />

        {/* The reset link follows the field in tab order but sits on the label row. */}
        <Box sx={{ position: 'relative' }}>
          <PasswordField
            id="login-password"
            label="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: undefined }));
            }}
            autoComplete="current-password"
            required
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password}
            inputRef={passwordRef}
            visible={showPassword}
            onVisibleChange={setShowPassword}
          />
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={openReset}
            sx={{ position: 'absolute', top: 0, right: 0, ...typeStyle('label-md') }}
          >
            Forgot password?
          </Link>
        </Box>

        {/* Not `disabled` while loading: a disabled button drops keyboard focus to <body>. */}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          aria-disabled={loading || undefined}
          aria-busy={loading || undefined}
          sx={{
            '&[aria-disabled="true"]': { bgcolor: sbg.action.primaryHover, color: sbg.text.onBrand, cursor: 'progress' },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" />
              <span>
                Signing in…
                {elapsed >= 3 && (
                  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {` ${elapsed}s`}
                  </Box>
                )}
              </span>
            </>
          ) : (
            'Sign in'
          )}
        </Button>
      </Box>

      <Dialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="reset-password-title"
        aria-describedby="reset-password-description"
      >
        <Box
          component="form"
          noValidate
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            event.stopPropagation();
            handleForgotPassword();
          }}
        >
          <DialogTitle id="reset-password-title">Reset your password</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2 }}>
            <DialogContentText id="reset-password-description">
              We’ll email you a link to set a new password.
            </DialogContentText>
            {resetSent && (
              <Alert severity="success" role="status">
                <AlertTitle>Reset link sent</AlertTitle>
                {RESET_EMAIL_SENT}
              </Alert>
            )}
            {reset.kind === 'failed' && (
              <Alert severity="error">
                <AlertTitle>Couldn’t send the reset link</AlertTitle>
                {reset.body}
              </Alert>
            )}
            <TextField
              id="reset-email"
              label="Work email"
              type="email"
              size="medium"
              placeholder="name@sbgfunding.com"
              autoComplete="email"
              autoFocus
              fullWidth
              value={resetEmail}
              onChange={(e) => {
                setResetEmail(e.target.value);
                if (resetFieldError) setReset({ kind: 'idle' });
              }}
              error={Boolean(resetFieldError)}
              helperText={resetFieldError ?? undefined}
              inputRef={resetEmailRef}
              sx={comfortFieldSx}
              slotProps={{ htmlInput: { required: true } }}
            />
          </DialogContent>
          <DialogActions>
            {resetSent ? (
              <>
                <Button type="submit" variant="text" disabled={resetLoading}>
                  {resetLoading ? 'Sending…' : 'Send again'}
                </Button>
                <Button type="button" variant="contained" onClick={() => setResetOpen(false)}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="text" onClick={() => setResetOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={resetLoading}
                  aria-busy={resetLoading || undefined}
                  startIcon={resetLoading ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
                >
                  {resetLoading ? 'Sending…' : 'Send reset link'}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </AuthLayout>
  );
};

export default Login;
